import type { AwardStatus, ProgressStatus, StatusConfig, StatusOption } from "../models/production";

export const DEFAULT_AWARD_STATUS_OPTIONS: StatusOption[] = [
  { code: "bidding", label: "Bidding", sortOrder: 10, isDefault: true },
  { code: "off", label: "Off", sortOrder: 20 },
  { code: "lost", label: "Lost", sortOrder: 30 },
  { code: "awarded", label: "Awarded", sortOrder: 40 },
  { code: "rebid", label: "Rebid", sortOrder: 50 },
  { code: "overage", label: "Overage", sortOrder: 60 },
  { code: "invoiced", label: "Invoiced", sortOrder: 70 },
];

export const DEFAULT_PROGRESS_STATUS_OPTIONS: StatusOption[] = [
  { code: "awaiting_turnover", label: "Awaiting Turnover", sortOrder: 10, isDefault: true },
  { code: "ready_to_start", label: "Ready to Start", sortOrder: 20 },
  { code: "in_progress", label: "In Progress", sortOrder: 30 },
  { code: "final", label: "Final", sortOrder: 40 },
];

export const DEFAULT_STATUS_CONFIG: StatusConfig = {
  id: "default-status-config",
  name: "Default Production Statuses",
  awardStatuses: DEFAULT_AWARD_STATUS_OPTIONS,
  progressStatuses: DEFAULT_PROGRESS_STATUS_OPTIONS,
  fptMappings: {
    awardStatuses: {
      pending: "Bidding",
      bidding: "Bidding",
      awarded: "Awarded",
      not_awarded: "Lost",
      lost: "Lost",
      off: "Off",
      rebid: "Rebid",
      overage: "Overage",
      invoiced: "Invoiced",
      potential_shot: "Potential Shot",
    },
    progressStatuses: {
      awaiting_turnover: "ap",
      ready_to_start: "rdy",
      in_progress: "ip",
      final: "fin",
    },
  },
  defaultAwardStatus: "bidding",
  defaultProgressStatus: "awaiting_turnover",
  createdAt: new Date(0).toISOString(),
  updatedAt: new Date(0).toISOString(),
};

const normalizeStatusValue = (value?: string | null) => value?.trim().toLowerCase().replace(/[\s-]+/g, "_");

export function buildStatusConfig(input?: Partial<StatusConfig> | null): StatusConfig {
  const base = DEFAULT_STATUS_CONFIG;
  return {
    ...base,
    ...input,
    awardStatuses: input?.awardStatuses?.length ? input.awardStatuses : DEFAULT_AWARD_STATUS_OPTIONS,
    progressStatuses: input?.progressStatuses?.length ? input.progressStatuses : DEFAULT_PROGRESS_STATUS_OPTIONS,
    fptMappings: {
      awardStatuses: {
        ...(base.fptMappings?.awardStatuses || {}),
        ...(input?.fptMappings?.awardStatuses || {}),
      },
      progressStatuses: {
        ...(base.fptMappings?.progressStatuses || {}),
        ...(input?.fptMappings?.progressStatuses || {}),
      },
    },
  };
}

export function findStatusOption(options: StatusOption[], value?: string | null): StatusOption | null {
  const normalized = normalizeStatusValue(value);
  if (!normalized) return null;
  return options.find(option => normalizeStatusValue(option.code) === normalized || normalizeStatusValue(option.label) === normalized) || null;
}

export function normalizeAwardStatus(value: unknown, config?: Partial<StatusConfig> | null): AwardStatus | undefined {
  if (typeof value !== "string") return buildStatusConfig(config).defaultAwardStatus;
  const option = findStatusOption(buildStatusConfig(config).awardStatuses, value);
  return option?.code || buildStatusConfig(config).defaultAwardStatus;
}

export function normalizeProgressStatus(value: unknown, config?: Partial<StatusConfig> | null): ProgressStatus | undefined {
  if (typeof value !== "string") return buildStatusConfig(config).defaultProgressStatus;
  const option = findStatusOption(buildStatusConfig(config).progressStatuses, value);
  return option?.code || buildStatusConfig(config).defaultProgressStatus;
}

export function mapLegacyBidStatusToSeparatedStatuses(status?: string | null, config?: Partial<StatusConfig> | null) {
  const normalized = normalizeStatusValue(status);
  const baseConfig = buildStatusConfig(config);

  switch (normalized) {
    case "awarded":
      return {
        awardStatus: normalizeAwardStatus("awarded", baseConfig),
        progressStatus: normalizeProgressStatus(undefined, baseConfig),
      };
    case "bidding":
      return {
        awardStatus: normalizeAwardStatus("bidding", baseConfig),
        progressStatus: normalizeProgressStatus(undefined, baseConfig),
      };
    case "off":
      return {
        awardStatus: normalizeAwardStatus("off", baseConfig),
        progressStatus: normalizeProgressStatus(undefined, baseConfig),
      };
    case "lost":
      return {
        awardStatus: normalizeAwardStatus("lost", baseConfig),
        progressStatus: normalizeProgressStatus(undefined, baseConfig),
      };
    case "rebid":
      return {
        awardStatus: normalizeAwardStatus("rebid", baseConfig),
        progressStatus: normalizeProgressStatus(undefined, baseConfig),
      };
    case "overage":
      return {
        awardStatus: normalizeAwardStatus("overage", baseConfig),
        progressStatus: normalizeProgressStatus(undefined, baseConfig),
      };
    case "invoiced":
      return {
        awardStatus: normalizeAwardStatus("invoiced", baseConfig),
        progressStatus: normalizeProgressStatus(undefined, baseConfig),
      };
    default:
      return {
        awardStatus: normalizeAwardStatus(undefined, baseConfig),
        progressStatus: normalizeProgressStatus(undefined, baseConfig),
      };
  }
}
